import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import Header from "../components/Header";
import Footer from "../components/Footer";
import {
  CheckCircle2,
  Phone,
  Home,
  Share2,
  Copy,
  MessageSquare,
  Mail as MailIcon,
  Gift,
} from "lucide-react";

export default function ThankYou() {
  const [params] = useSearchParams();
  const id = params.get("id");
  const [copied, setCopied] = useState(false);

  const referralUrl = useMemo(() => {
    if (typeof window === "undefined" || !id) return "";
    return `${window.location.origin}/start?ref=${id}`;
  }, [id]);

  const copyLink = async () => {
    if (!referralUrl) return;
    try {
      await navigator.clipboard.writeText(referralUrl);
      setCopied(true);
      toast.success("Referral link copied");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Couldn't copy — please copy manually");
    }
  };

  const smsHref = referralUrl
    ? `sms:?&body=${encodeURIComponent(
        `I just used Car Hurt Help after my accident — they helped right away. If you were in a wreck and weren't at fault, fill this out: ${referralUrl}`
      )}`
    : "#";

  const mailHref = referralUrl
    ? `mailto:?subject=${encodeURIComponent(
        "Car Hurt Help — quick intake after a car accident"
      )}&body=${encodeURIComponent(
        `Hey — I just used Car Hurt Help after my accident and they were great. If you were in a wreck and weren't at fault, take 3 minutes and fill this out: ${referralUrl}`
      )}`
    : "#";

  return (
    <div className="min-h-screen flex flex-col" data-testid="thank-you-page">
      <Header />
      <main className="flex-1 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24 text-center">
        <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-[#166534]/10">
          <CheckCircle2 className="h-9 w-9 text-[#166534]" />
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-[#0B192C] mt-6 tracking-tight">
          Thank you. We've got it.
        </h1>
        <p className="text-[#4B5563] mt-4 text-base sm:text-lg max-w-xl mx-auto">
          Your submission has been securely received. A Car Hurt Help team
          member will reach out within one business day — typically the same
          day. Keep your phone close.
        </p>

        <div className="mt-10 grid sm:grid-cols-2 gap-4 max-w-xl mx-auto text-left">
          <div className="rounded-xl border border-[#E2E8F0] bg-white p-5">
            <Phone className="h-5 w-5 text-[#0B192C]" />
            <div className="text-xs uppercase tracking-[0.2em] text-[#4B5563] mt-3">
              What's next
            </div>
            <div className="font-semibold text-[#0B192C] mt-1">
              We'll be in touch
            </div>
            <p className="text-sm text-[#4B5563] mt-1">
              An intake specialist will reach out shortly to finish the next steps.
            </p>
          </div>
          <div className="rounded-xl border border-[#E2E8F0] bg-white p-5">
            <MailIcon className="h-5 w-5 text-[#0B192C]" />
            <div className="text-xs uppercase tracking-[0.2em] text-[#4B5563] mt-3">
              Confirmation
            </div>
            <div className="font-semibold text-[#0B192C] mt-1">
              Check your email
            </div>
            <p className="text-sm text-[#4B5563] mt-1">
              We've sent a confirmation to the email you provided.
            </p>
          </div>
        </div>

        {/* Referral block */}
        {referralUrl && (
          <section
            data-testid="referral-block"
            className="mt-14 rounded-2xl border-2 border-[#0B192C] bg-[#0B192C] text-white p-7 sm:p-10 text-left relative overflow-hidden"
          >
            <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-[#D32F2F]/20 blur-3xl pointer-events-none" />
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-[#D32F2F]/20 flex items-center justify-center">
                <Gift className="h-5 w-5 text-[#FCA5A5]" />
              </div>
              <div className="text-xs uppercase tracking-[0.22em] font-bold text-[#FCA5A5]">
                Help a friend out
              </div>
            </div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold mt-4 tracking-tight">
              Was someone with you, or do you know someone else who was in a wreck?
            </h2>
            <p className="text-slate-300 mt-2 max-w-xl">
              Share your personal link — anyone who fills out the form through it
              will be tagged to your submission, so our team can connect the cases.
            </p>

            <div className="mt-6 flex flex-col sm:flex-row gap-2 sm:items-center">
              <div className="flex-1 bg-white/10 ring-1 ring-white/20 rounded-md px-3 py-3 text-sm text-white overflow-hidden">
                <div className="truncate" data-testid="referral-link-text">
                  {referralUrl}
                </div>
              </div>
              <button
                onClick={copyLink}
                data-testid="referral-copy-button"
                className="inline-flex items-center justify-center gap-2 rounded-md bg-[#D32F2F] hover:bg-[#B71C1C] text-white font-semibold px-5 py-3 transition-colors"
              >
                {copied ? (
                  <>
                    <CheckCircle2 className="h-4 w-4" /> Copied
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4" /> Copy link
                  </>
                )}
              </button>
            </div>

            <div className="mt-4 grid sm:grid-cols-2 gap-2">
              <a
                href={smsHref}
                data-testid="referral-sms-button"
                className="inline-flex items-center justify-center gap-2 rounded-md bg-white/10 hover:bg-white/15 text-white font-semibold px-5 py-3 ring-1 ring-white/20 transition-colors"
              >
                <MessageSquare className="h-4 w-4" /> Send via text
              </a>
              <a
                href={mailHref}
                data-testid="referral-email-button"
                className="inline-flex items-center justify-center gap-2 rounded-md bg-white/10 hover:bg-white/15 text-white font-semibold px-5 py-3 ring-1 ring-white/20 transition-colors"
              >
                <MailIcon className="h-4 w-4" /> Send via email
              </a>
            </div>

            <div className="mt-5 flex items-center gap-2 text-xs text-slate-300">
              <Share2 className="h-3.5 w-3.5" />
              Your link: <span className="font-mono text-white">{id?.slice(0, 8)}…</span>
            </div>
          </section>
        )}

        <Link
          to="/"
          data-testid="thank-you-home-button"
          className="mt-12 inline-flex items-center gap-2 rounded-md bg-white border border-[#E2E8F0] hover:bg-[#F1F5F9] text-[#0B192C] font-semibold px-6 py-3 transition-colors"
        >
          <Home className="h-4 w-4" /> Back to home
        </Link>
      </main>
      <Footer />
    </div>
  );
}
